import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { MotionConfig } from 'motion/react';
import { UserButton, useAuth, useUser } from '@clerk/clerk-react';
import { useGameStore } from '../store/gameStore';
import { Button } from '../components/ui/button';
import { FolderOpen, BookOpen, Play } from 'lucide-react';
import { gameAPI } from '../services/gameAPI';
import { useToast } from '../hooks/use-toast';
import { PrivacyPolicyModal } from '../components/modals/PrivacyPolicyModal';
import { TutorialThemeModal } from '../components/modals/TutorialThemeModal';
import { TrailerModal } from '../components/modals/TrailerModal';
import DisplayNameEditor from '../components/profile/DisplayNameEditor';
import FeaturedCaseCard from '../components/featured/FeaturedCaseCard';
import FreshCaseCard from '../components/home/FreshCaseCard';
import CaseStack from '../components/home/CaseStack';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { listFeaturedCases } from '../services/featuredCaseAPI';
import { THEME_STYLES, getThemeStyle } from '../components/home/themeStyles';

// Content per generated theme. Card styling comes from the shared table so the
// Fresh and Featured cards cannot drift apart.
const themes = [
  {
    ...THEME_STYLES.noir,
    id: 'noir',
    name: 'Film Noir',
    era: '1940s',
    description: 'Shadows, secrets, and smoky jazz clubs in post-war America',
    image: '/images/themes/noir-theme.jpg',
    bgImage: '/images/themes/noir-bg.jpg',
  },
  {
    ...THEME_STYLES['90s_nyc'],
    id: '90s_nyc',
    name: '90s NYC',
    era: '1990s',
    description: 'Gritty streets, hip-hop beats, and urban crime in New York City',
    image: 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=600&h=800&fit=crop',
    bgImage: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&h=1080&fit=crop', // NYC streets
  },
  {
    ...THEME_STYLES.miami_vice,
    id: 'miami_vice',
    name: 'Miami Vice',
    era: '1980s',
    description: 'Neon lights, cocaine cowboys, and pastel suits in the Magic City',
    image: '/images/themes/miami-theme.jpg',
    bgImage: '/images/themes/miami-bg.jpg',
  },
];

// Module-level constants so a cleared list is referentially stable and an
// identity-mismatched read cannot allocate a new array on every render.
const NO_FEATURED_CASES = [];
const EMPTY_FEATURED_STATE = { ownerId: null, cases: NO_FEATURED_CASES };
// Stacks replace the two rows from Tailwind's `md` breakpoint up -- the width at
// which the grid goes from one column to three. Phones keep today's list.
const STACK_LAYOUT_QUERY = '(min-width: 768px)';
// The open stack lives in the address so browser back, reload and returning
// from the difficulty picker all land where the player left.
const STACK_PARAM = 'stack';
const STACK_IDS = ['fresh', 'featured'];
// Marks a history entry THIS page pushed when opening a stack. Closing pops
// such an entry (same as browser back); an entry we did not push (a reload or
// a direct link) is replaced instead, so on-screen Back never leaves the site.
const STACK_PUSHED_STATE_KEY = 'homeStackPushed';
const FEATURED_STACK_GRACE_MS = 1500;
const STACK_COPY = {
  fresh: { label: 'Fresh Cases', subtitle: 'A new case built for you every time.' },
  featured: { label: 'Featured Cases', subtitle: 'Curated investigations ready to play now.' },
};

export const ThemeSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  // Identity for the featured fetch: `useAuth().userId` is the Clerk handle the
  // per-player featured status is keyed on. `useUser().user` is only used for
  // the display name below and is not the identity source of truth here.
  const { isLoaded: authLoaded, isSignedIn, userId } = useAuth();
  const { setTheme } = useGameStore();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const isStackLayout = useMediaQuery(STACK_LAYOUT_QUERY);
  // A second activation before the first navigation has rendered (double
  // click, or two Back presses while the async popstate is pending) must not
  // push or pop a second history entry. Cleared once the address settles.
  const stackNavPendingRef = useRef(false);
  // Per-player loaded state is stored WITH the Clerk userId it was loaded for,
  // and read back through an owner check below. The clear-then-refetch plus
  // `cancelled` pattern the effects use is correct as far as it goes -- it stops
  // a stale RESPONSE being written -- but it cannot close the other window:
  // between Clerk swapping the identity and the effect running its clear there
  // is a committed RENDER in which the new userId is current while the previous
  // player's data is still in state. In that render user B saw A's
  // "In progress"/"Resume" featured cards, A's "Continue Tutorial (Step N)"
  // label and, worst of all, A's `caseGenerationUnlocked`. Tagging the owner
  // makes that structurally impossible instead of dependent on effect ordering.
  const [tutorialStatusState, setTutorialStatusState] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  // Distinct from "locked": we could not reach the backend at all, so we know
  // nothing about this player's progress. Never conflate the two in the UI.
  const [statusUnavailableState, setStatusUnavailableState] = useState(false);
  // The userId the three values above describe. `null` until a response lands.
  const [statusOwnerId, setStatusOwnerId] = useState(null);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTutorialThemeModal, setShowTutorialThemeModal] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [shouldResetTutorial, setShouldResetTutorial] = useState(false);
  const [featuredState, setFeaturedState] = useState(EMPTY_FEATURED_STATE);
  const [featuredGraceState, setFeaturedGraceState] = useState({ ownerId: null, expired: false });

  // The only reads the render is allowed to use. When the tag does not match the
  // current identity we fall back to the pre-answer rendering -- `loading`, no
  // status, nothing unavailable -- which is exactly what a first mount shows and
  // is the honest "we have not heard back for THIS player yet" state.
  const ownsTutorialStatus = statusOwnerId === userId;
  const tutorialStatus = ownsTutorialStatus ? tutorialStatusState : null;
  const loading = ownsTutorialStatus ? loadingState : true;
  const statusUnavailable = ownsTutorialStatus ? statusUnavailableState : false;
  const featuredCases =
    featuredState.ownerId === userId ? featuredState.cases : NO_FEATURED_CASES;
  // One status for every Fresh card, from the owner-tagged reads above. Order
  // matters: `loading` wins (no claim about progress yet), then unlocked, then
  // the two lock flavours -- "unavailable" when the backend did not answer.
  const freshStatus = loading
    ? 'loading'
    : tutorialStatus?.caseGenerationUnlocked
      ? 'unlocked'
      : statusUnavailable
        ? 'unavailable'
        : 'locked';

  // `featuredState.ownerId === userId` only once THIS player's fetch settled
  // (success or failure); until then the Featured stack is "still loading".
  const featuredSettled = featuredState.ownerId === userId;
  const featuredGraceExpired = featuredGraceState.ownerId === userId && featuredGraceState.expired;
  const hasFeatured = featuredCases.length > 0;
  // Zero featured cases (settled empty): no stacks, the Fresh row directly,
  // exactly as the pre-featured layout did.
  const showStacks = isStackLayout && !(featuredSettled && !hasFeatured);
  const stackParam = searchParams.get(STACK_PARAM);
  const requestedStack = STACK_IDS.includes(stackParam) ? stackParam : null;
  // The EFFECTIVE open stack. The address is only a request: an invalid value
  // is closed, and `featured` with no featured cards loaded falls back to the
  // closed view. UI state only -- it carries no data across identities.
  let openStack = null;
  if (showStacks && requestedStack === 'fresh') openStack = 'fresh';
  if (showStacks && requestedStack === 'featured' && hasFeatured) openStack = 'featured';

  // Announce address-driven open/close changes, not initial render, data loads,
  // or a resize that only swaps between stacks and the phone list.
  const [stackAnnouncement, setStackAnnouncement] = useState('');
  const [announcementState, setAnnouncementState] = useState({ requestedStack, openStack });
  if (announcementState.requestedStack !== requestedStack || announcementState.openStack !== openStack) {
    setAnnouncementState({ requestedStack, openStack });
    if (announcementState.requestedStack !== requestedStack && announcementState.openStack !== openStack) {
      setStackAnnouncement(
        openStack
          ? `${STACK_COPY[openStack].label} opened`
          : `${STACK_COPY[announcementState.openStack].label} closed`,
      );
    }
  }

  useEffect(() => {
    stackNavPendingRef.current = false;
  }, [requestedStack]);

  const openStackFor = (stackId) => {
    if (stackNavPendingRef.current || requestedStack === stackId) return;
    stackNavPendingRef.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(STACK_PARAM, stackId);
        return next;
      },
      { state: { [STACK_PUSHED_STATE_KEY]: true } },
    );
  };

  const closeStack = useCallback(() => {
    if (stackNavPendingRef.current || !requestedStack) return;
    stackNavPendingRef.current = true;
    if (location.state?.[STACK_PUSHED_STATE_KEY] === true) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(STACK_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [requestedStack, location.state, navigate, setSearchParams]);

  // Escape closes the open stack (spec §6). Only while one is open; never while
  // a dialog is open -- the dialog owns Escape and closes itself first.
  useEffect(() => {
    if (!openStack) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (showPrivacyPolicy || showTutorialThemeModal || showTrailer) return;
      if (event.target instanceof Element && event.target.closest(
        'input, textarea, select, [contenteditable], [data-home-profile], .cl-rootBox, .cl-userButtonPopoverCard, [role="dialog"]',
      )) return;
      closeStack();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openStack, showPrivacyPolicy, showTutorialThemeModal, showTrailer, closeStack]);

  // Grid placement inside a 6-column row: a 2-column span there is exactly one
  // column of today's 3-column grid ((W - 2·gap) / 3), so a closed stack's
  // cards are today's card width. The hidden stack stays in row 1 under the
  // open one (explicit placement may overlap) so it never wraps to a new row
  // while it fades out. Paired stacks are nudged 2rem outward (relative
  // offset, so card width is unchanged) to widen the gap between them.
  const stackSlotClass = (stackId) => {
    if (openStack === stackId) return 'col-span-6 col-start-1 row-start-1 relative z-10';
    if (openStack) {
      return stackId === 'fresh'
        ? 'col-span-2 col-start-2 row-start-1 relative -left-8'
        : 'col-span-2 col-start-4 row-start-1 relative left-8';
    }
    if (stackId === 'fresh') return hasFeatured ? 'col-span-2 col-start-2 relative -left-8' : 'col-span-2 col-start-3';
    return 'col-span-2 col-start-4 relative left-8';
  };

  // Tutorial status is per-player too, and it drives more than a label: theme
  // unlock (`caseGenerationUnlocked`), the lock overlay and the "Continue Tutorial
  // (Step N)" button all read it. This used to be `[]`-deps "on mount only", which
  // has the identical defect the featured fetch below was fixed for -- Clerk can
  // swap the signed-in user without unmounting this page, so player B inherited
  // player A's unlock state and step number. Keyed on the same three primitives.
  //
  // The identity transition clears the status and returns to `loading` FIRST, so
  // the stale answer is gone before the refetch is issued; `loading` suppresses
  // both the lock overlay and the unlock helper text, which is the correct
  // "we have not heard back yet" rendering (and is exactly what first mount shows).
  // A signed-out or still-loading Clerk state sends no request and stays in that
  // state -- this page only ever mounts inside `<SignedIn>` (App.js:97), so that
  // branch is the harness/transition case, not a reachable player state.
  //
  // `cancelled` is load-bearing, not ceremony: without it user A's in-flight
  // response resolves after the clear and writes A's unlock state and step number
  // back over B's blank slate. The featured effect below uses the same pattern.
  // It is kept alongside the owner tag as defence in depth, not replaced by it:
  // the tag makes a mismatched write unrenderable, `cancelled` stops the write
  // happening at all.
  //
  // The last two clears are a different KIND of state and need their own
  // justification. Everything above is FETCHED state, which the owner tag
  // protects: a value carrying the wrong owner is simply not read by the render.
  // `showTutorialThemeModal` and `shouldResetTutorial` are user INTENT, recorded
  // from a click rather than from a response, so there is nothing to tag them
  // against and the owner mechanism does not cover them at all. Leaving them set
  // across an identity change is not a stale-label problem, it is destructive:
  // user A clicks "Restart"/"Replay" (arming `shouldResetTutorial`), the account
  // switches while the theme modal is still open, and the next theme pick runs
  // A's reset against B's account -- deleting B's tutorial session. Intent
  // belongs to the identity that expressed it and does not survive that identity
  // going away.
  useEffect(() => {
    setTutorialStatusState(null);
    setStatusUnavailableState(false);
    setLoadingState(true);
    setStatusOwnerId(null);
    setShowTutorialThemeModal(false);
    setShouldResetTutorial(false);

    if (!authLoaded || !isSignedIn || !userId) {
      return undefined;
    }

    let cancelled = false;
    // Captured, not read from the closure later: this is the identity the
    // response about to be requested will describe.
    const ownerId = userId;

    const checkTutorialStatus = async () => {
      try {
        const status = await gameAPI.getTutorialStatus();
        if (!cancelled) {
          setTutorialStatusState(status);
          setStatusUnavailableState(false);
          setLoadingState(false);
          setStatusOwnerId(ownerId);
        }
      } catch (error) {
        console.error('Failed to check tutorial status:', error);
        // This used to synthesise `caseGenerationUnlocked: true` and call it
        // graceful degradation. It was not degradation, it was a fabrication: it
        // rendered a fully working UI over a backend that was not answering, sent
        // the player into a case-generation flow that could only fail, and made a
        // real outage invisible to anyone watching the app. Report the truth --
        // we do not know this player's status -- and let the UI say so.
        if (!cancelled) {
          setTutorialStatusState(null);
          setStatusUnavailableState(true);
          setLoadingState(false);
          setStatusOwnerId(ownerId);
        }
      }
    };

    checkTutorialStatus();
    return () => {
      cancelled = true;
    };
  }, [authLoaded, isSignedIn, userId]);

  useEffect(() => {
    if (requestedStack !== 'featured' || featuredSettled || !authLoaded || !isSignedIn || !userId) {
      return undefined;
    }
    setFeaturedGraceState({ ownerId: userId, expired: false });
    const timer = setTimeout(() => {
      setFeaturedGraceState({ ownerId: userId, expired: true });
    }, FEATURED_STACK_GRACE_MS);
    return () => clearTimeout(timer);
  }, [requestedStack, featuredSettled, authLoaded, isSignedIn, userId]);

  // Featured-case statuses are per-player ("In progress"/"Resume"), so this fetch is
  // keyed on the Clerk identity, not run once on mount. Clerk can swap the signed-in
  // user without unmounting this page (multi-session / account switcher); with `[]`
  // deps the previous player's progress stayed on screen for the next one.
  //
  // Every identity transition clears the list FIRST, so the stale cards are gone
  // before the refetch is even issued rather than lingering while it is in flight.
  // The empty list also hides the whole band (see the `featuredCases.length > 0`
  // guard below), which is exactly the existing fetch-failure behaviour, and it is
  // what a signed-out or still-loading Clerk state renders -- no request is sent in
  // those states. Deps are three primitives, so the effect cannot re-run off its own
  // `setFeaturedState` call and there is no fetch loop.
  //
  // The clear is unconditional, matching the tutorial effect above. It used to be a
  // no-op-preserving functional update; that asymmetry invited the reading that one
  // form mattered. Neither does: the owner tag is what carries correctness now, and
  // the reset value is a module constant so the cleared state is referentially
  // stable anyway.
  useEffect(() => {
    setFeaturedState(EMPTY_FEATURED_STATE);

    if (!authLoaded || !isSignedIn || !userId) {
      return undefined;
    }

    let cancelled = false;
    const ownerId = userId;

    const loadFeaturedCases = async () => {
      try {
        const response = await listFeaturedCases();
        if (!cancelled) {
          setFeaturedState({
            ownerId,
            cases: Array.isArray(response?.cases) ? response.cases : NO_FEATURED_CASES,
          });
        }
      } catch (error) {
        // Swallow the rejection so a featured-list outage cannot take the Fresh
        // cards down with it, and say so somewhere a human will see it.
        console.warn('Failed to load featured cases:', error);
        // Settle the list as EMPTY for this owner. On phones this changes
        // nothing (the unconditional clear above already emptied it, so the
        // band stays hidden). On >= md it is what separates "failed/none" --
        // render the Fresh row directly -- from "still loading", which shows
        // the Fresh stack alone and waits. Without it a failed fetch looked
        // like loading forever.
        if (!cancelled) {
          setFeaturedState({ ownerId, cases: NO_FEATURED_CASES });
        }
      }
    };

    loadFeaturedCases();
    return () => {
      cancelled = true;
    };
  }, [authLoaded, isSignedIn, userId]);

  // Show completion message if redirected from tutorial. Keyed on the flag,
  // not the whole location: opening/closing a stack changes the address (and
  // keeps `tutorialComplete` in it), which must not re-show the banner.
  const tutorialJustCompleted = searchParams.get('tutorialComplete') === 'true';
  useEffect(() => {
    if (!tutorialJustCompleted) return undefined;
    setShowCompletionMessage(true);
    const timer = setTimeout(() => setShowCompletionMessage(false), 5000);
    return () => clearTimeout(timer);
  }, [tutorialJustCompleted]);

  const handleThemeSelect = (themeId) => {
    if (!tutorialStatus?.caseGenerationUnlocked) {
      // Don't allow selection - the lock overlay handles the visual feedback
      return;
    }
    setTheme(themeId);
    // From the open Fresh stack, tell the difficulty page where "Back" goes so
    // its on-screen Back lands on the open stack too (browser back already does).
    if (openStack === 'fresh') {
      navigate('/difficulty', { state: { homeStack: 'fresh' } });
      return;
    }
    navigate('/difficulty');
  };

  const handleFeaturedCaseSelect = (caseId) => {
    navigate(`/featured/${caseId}`);
  };

  const freshStackCards = themes.map((theme, index) => ({
    key: theme.id,
    glowRgb: theme.accentRgb,
    node: (
      <FreshCaseCard theme={theme} index={index} status={freshStatus} onSelect={handleThemeSelect} />
    ),
  }));
  const featuredStackCards = featuredCases.map((caseInfo) => ({
    key: caseInfo.id,
    glowRgb: getThemeStyle(caseInfo.theme).accentRgb,
    node: <FeaturedCaseCard caseInfo={caseInfo} onOpen={handleFeaturedCaseSelect} />,
  }));

  const handleStartTutorial = async (forceReset = false) => {
    // Store whether we need to reset tutorial
    setShouldResetTutorial(forceReset);

    // Show theme selection modal
    setShowTutorialThemeModal(true);
  };

  const handleTutorialThemeSelect = async (selectedTheme) => {
    // Reset tutorial if needed
    if (shouldResetTutorial && tutorialStatus?.hasTutorialSession) {
      try {
        await gameAPI.resetTutorial();
      } catch (error) {
        console.error('Failed to reset tutorial:', error);
        toast({
          title: 'Reset failed',
          description: 'Could not reset tutorial. Starting from current progress.',
          variant: 'destructive'
        });
      }
    }

    // Navigate to tutorial page with selected theme
    navigate('/tutorial', { state: { theme: selectedTheme } });
  };

  return (
    <MotionConfig reducedMotion="user">
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #c19a6b 0%, #b8936f 25%, #c19a6b 50%, #ab8562 75%, #c19a6b 100%)',
        backgroundSize: '400% 400%',
      }}
    >
      {/* Cork texture overlay */}
      <div 
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(139, 90, 43, 0.1) 2px,
              rgba(139, 90, 43, 0.1) 4px
            ),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(139, 90, 43, 0.1) 2px,
              rgba(139, 90, 43, 0.1) 4px
            )
          `,
          backgroundSize: '20px 20px',
        }}
      ></div>

      {/* Tutorial Completion Message */}
      {showCompletionMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slide-down">
          🎉 Tutorial completed! You're ready for real cases.
        </div>
      )}

      {/* User Profile - Top Right Corner */}
      <div data-home-profile className="absolute top-4 right-4 z-50 flex items-start gap-3">
        <DisplayNameEditor
          compact
          initialFallbackName={user?.fullName || user?.firstName || ''}
        />
        <div className="bg-white/90 rounded-full p-1 shadow-lg border-2 border-amber-700/30">
          <UserButton 
            afterSignOutUrl="/sign-in"
            appearance={{
              elements: {
                avatarBox: 'w-10 h-10',
              }
            }}
          />
        </div>
      </div>

      {/* Container to keep decorative items near cards */}
      <div className="relative max-w-6xl w-full">
        {/* Decorative pinned items - LARGER SIZES matching loading screen */}
        
        {/* Large Evidence Polaroid - Top Left */}
        <div className="absolute -top-24 -left-24 w-56 h-64 bg-white shadow-2xl rotate-[-12deg] transform hover:scale-105 transition-transform duration-300 z-10">
          <img 
            src="https://images.unsplash.com/photo-1575936123452-b67c3203c357?w=400&h=400&fit=crop" 
            alt="Evidence"
            className="w-full h-52 object-cover"
          />
          <div className="p-3 text-center">
            <p className="text-base font-handwriting text-gray-700 font-bold">EVIDENCE #601</p>
          </div>
        </div>

        {/* Large Yellow Post-it Note - Top Right */}
        <div className="absolute -top-32 -right-20 w-64 h-64 bg-yellow-300 shadow-2xl rotate-[8deg] p-6 transform hover:rotate-[6deg] transition-transform duration-300 z-10">
          <p className="text-lg font-handwriting text-gray-800 leading-relaxed">
            3 ACTIVE CASES<br/>
            ALL UNSOLVED.<br/>
            TIME TO CRACK<br/>
            THEM.
          </p>
        </div>

        {/* Large Red Evidence Folder - Bottom Left */}
        <div className="absolute -bottom-24 -left-20 w-72 h-80 bg-red-100 border-4 border-red-700 shadow-2xl rotate-[-5deg] p-4 transform hover:scale-105 transition-transform duration-300 z-10">
          <div className="border-4 border-dashed border-red-700 h-full flex flex-col items-center justify-center gap-3">
            <p className="text-2xl font-bold text-red-700 text-center tracking-wider">CASE FILE</p>
            <div className="w-16 h-16 border-4 border-red-700 rounded-full flex items-center justify-center">
              <p className="text-red-700 font-bold text-xs text-center">DO NOT<br/>REMOVE</p>
            </div>
            <p className="text-sm font-bold text-red-700">CLASSIFIED</p>
          </div>
        </div>

        {/* Large Clue Polaroid - Bottom Right */}
        <div className="absolute -bottom-20 -right-24 w-52 h-60 bg-white shadow-2xl rotate-[10deg] transform hover:scale-105 transition-transform duration-300 z-10">
          <img 
            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=400&fit=crop" 
            alt="Clue"
            className="w-full h-48 object-cover"
          />
          <div className="p-2 text-center">
            <p className="text-base font-handwriting text-gray-700 font-bold">Check Timelines</p>
          </div>
        </div>

        {/* Large Pink Post-it Note - Left Side */}
        <div className="absolute top-1/4 -left-28 w-56 h-56 bg-pink-200 shadow-2xl rotate-[-15deg] p-5 transform hover:rotate-[-12deg] transition-transform duration-300 z-10">
          <p className="text-base font-handwriting text-gray-800 leading-relaxed">
            CHECK MOTIVE<br/>
            TIMELINE<br/>
            TESTIMONY!!
          </p>
        </div>

        {/* Confidential Files Folder - Right Side */}
        <div className="absolute bottom-1/3 -right-20 w-60 h-48 bg-yellow-100 shadow-2xl rotate-[12deg] transform hover:rotate-[10deg] transition-transform duration-300 z-10 overflow-hidden border-2 border-amber-700">
          <div className="bg-amber-700 text-white text-center py-2 text-xs font-bold tracking-widest">CONFIDENTIAL FILES</div>
          <div className="p-4 space-y-1">
            <div className="h-2 bg-amber-700/30 w-full"></div>
            <div className="h-2 bg-amber-700/30 w-5/6"></div>
            <div className="h-2 bg-amber-700/30 w-4/5"></div>
            <div className="h-2 bg-amber-700/30 w-full"></div>
          </div>
        </div>

        {/* Push Pins scattered */}
        <div className="absolute top-10 left-1/4 w-6 h-6 rounded-full bg-red-600 shadow-lg z-20"></div>
        <div className="absolute top-20 right-1/3 w-5 h-5 rounded-full bg-blue-500 shadow-lg z-20"></div>
        <div className="absolute bottom-32 left-1/3 w-6 h-6 rounded-full bg-yellow-400 shadow-lg z-20"></div>

        {/* Paper clips */}
        <div className="absolute top-1/3 right-1/4 w-12 h-20 border-4 border-gray-400 rounded-full rotate-45 z-[5]"></div>
        <div className="absolute bottom-1/4 left-1/4 w-10 h-16 border-4 border-gray-500 rounded-full rotate-[-30deg] z-[5]"></div>

        {/* Title */}
        <div className="text-center mb-12 space-y-4 relative z-20">
          <h1 className="text-7xl md:text-8xl font-bold tracking-wider text-gray-900 drop-shadow-[0_4px_4px_rgba(255,255,255,0.3)]"
              style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(255,255,255,0.5)' }}>
            MysHaps
          </h1>
          <p className="text-2xl text-gray-700 tracking-wide drop-shadow-sm font-light italic">Mystery Happens, Inc.</p>
          <p className="text-xl text-gray-800 tracking-wide drop-shadow-sm font-semibold pt-2">Choose Your Case</p>
        </div>

        {showStacks ? (
          <div data-testid="case-stacks" className="grid grid-cols-6 gap-8 w-full relative z-20">
            <CaseStack
              id="fresh"
              label={STACK_COPY.fresh.label}
              subtitle={STACK_COPY.fresh.subtitle}
              cards={freshStackCards}
              isOpen={openStack === 'fresh'}
              hidden={openStack === 'featured' || (requestedStack === 'featured' && !featuredSettled && !featuredGraceExpired)}
              onOpen={() => openStackFor('fresh')}
              onClose={closeStack}
              topCardState={freshStatus === 'unlocked' ? 'ready' : freshStatus}
              side={hasFeatured ? 'left' : 'center'}
              className={stackSlotClass('fresh')}
              openTestId="theme-grid"
            />
            {hasFeatured && (
              <CaseStack
                id="featured"
                label={STACK_COPY.featured.label}
                subtitle={STACK_COPY.featured.subtitle}
                cards={featuredStackCards}
                isOpen={openStack === 'featured'}
                hidden={openStack === 'fresh'}
                onOpen={() => openStackFor('featured')}
                onClose={closeStack}
                topCardState="ready"
                side="right"
                enterOnMount
                className={stackSlotClass('featured')}
                openTestId="featured-cases-band"
              />
            )}
          </div>
        ) : (
          <>
            {/* Theme Cards -- phones, and >= md when there are no featured cases */}
            <div data-testid="theme-grid" className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full relative z-20">
              {themes.map((theme, index) => (
                <FreshCaseCard
                  key={theme.id}
                  theme={theme}
                  index={index}
                  status={freshStatus}
                  onSelect={handleThemeSelect}
                />
              ))}
            </div>

            {hasFeatured && (
              <section data-testid="featured-cases-band" className="mt-12 relative z-20">
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">Featured Cases</h2>
                  <p className="mt-2 text-gray-800">Curated investigations ready to play now.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                  {featuredCases.map((caseInfo) => (
                    <FeaturedCaseCard
                      key={caseInfo.id}
                      caseInfo={caseInfo}
                      onOpen={handleFeaturedCaseSelect}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <p
          data-testid="case-stack-announcer"
          role="status"
          aria-live="polite"
          className="sr-only"
        >
          {stackAnnouncement}
        </p>

      {/* Footer with Tutorial and Archive Buttons */}
      <div className="mt-8 md:mt-12 text-center relative z-20 space-y-4">
        {/* Tutorial and Archive Buttons */}
        <div className="flex gap-3 md:gap-4 justify-center items-center flex-wrap px-4">
          {/* Tutorial Button - Shows different states */}
          {!tutorialStatus?.tutorialCompleted && tutorialStatus?.currentStep > 1 ? (
            // In-progress tutorial - show continue and restart options
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="min-h-touch bg-green-600 hover:bg-green-700 text-white border-green-700 text-sm md:text-base"
                onClick={() => handleStartTutorial(false)}
                disabled={loading}
              >
                <BookOpen className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Continue Tutorial (Step {tutorialStatus?.currentStep})</span>
                <span className="sm:hidden">Continue</span>
              </Button>
              <Button
                variant="outline"
                className="min-h-touch bg-gray-600 hover:bg-gray-700 text-white border-gray-700"
                onClick={() => handleStartTutorial(true)}
                disabled={loading}
              >
                Restart
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className="min-h-touch bg-blue-600 hover:bg-blue-700 text-white border-blue-700 text-sm md:text-base"
              onClick={() => handleStartTutorial(tutorialStatus?.tutorialCompleted)}
              disabled={loading}
            >
              <BookOpen className="w-4 h-4 mr-1 md:mr-2" />
              {loading ? 'Loading...' : tutorialStatus?.tutorialCompleted ? 'Replay Tutorial' : 'Start Tutorial'}
            </Button>
          )}

          {/* Archive Button */}
          <Button
            variant="outline"
            className="min-h-touch bg-white/80 hover:bg-white text-gray-800 hover:text-gray-800 border-gray-600 text-sm md:text-base"
            onClick={() => navigate('/archive')}
          >
            <FolderOpen className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Case Archive</span>
            <span className="sm:hidden">Archive</span>
          </Button>

          {/* Trailer Button */}
          <Button
            variant="outline"
            className="min-h-touch bg-white/80 hover:bg-white text-gray-800 hover:text-gray-800 border-gray-600 text-sm md:text-base"
            onClick={() => setShowTrailer(true)}
          >
            <Play className="w-4 h-4 mr-1 md:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">Watch the trailer</span>
            <span className="sm:hidden">Trailer</span>
          </Button>
        </div>

        {/* Helper text for locked users. Gated on having actually heard back --
            see the lock overlay above for why. */}
        {!loading && !statusUnavailable && !tutorialStatus?.caseGenerationUnlocked && (
          <p className="text-gray-800 text-sm drop-shadow-sm font-medium">
            ⬆️ Complete the tutorial to unlock case generation
          </p>
        )}

        {/* The honest replacement for the fabricated status. Says what is true:
            we could not reach the service, so nothing here is trustworthy yet. */}
        {!loading && statusUnavailable && (
          <p className="text-gray-800 text-sm drop-shadow-sm font-medium" role="status">
            We couldn&apos;t reach the case service. Your progress is safe — please try again shortly.
          </p>
        )}

        <p className="text-gray-800 text-sm drop-shadow-sm font-medium">An immersive murder mystery experience</p>

        {/* Privacy Policy Link */}
        <button
          onClick={() => setShowPrivacyPolicy(true)}
          className="text-gray-700 hover:text-gray-900 text-sm underline underline-offset-2 transition-colors"
        >
          Privacy Policy
        </button>
      </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />

      {/* Tutorial Theme Selection Modal */}
      <TutorialThemeModal
        isOpen={showTutorialThemeModal}
        onClose={() => setShowTutorialThemeModal(false)}
        onThemeSelect={handleTutorialThemeSelect}
      />

      {/* Trailer Modal */}
      <TrailerModal open={showTrailer} onOpenChange={setShowTrailer} />
    </div>
    </MotionConfig>
  );
};
